import React, { useEffect, useRef } from "react";
import Layout from "../../../components/vendor/Layout";
import Helmet from "react-helmet";
import Table from "../../../components/ui/Table";
import usePaginate from "../../../hooks/api/usePaginate";
import apiRequest from "../../../utils/vendor/apiRequest";
import useModal from "../../../hooks/ui/useModal";
import Pagination from "../../../components/ui/Pagination";
import { FaEdit, FaPlus, FaTrashAlt } from "react-icons/fa";
import CreateCateteriaInventory from "../../../components/vendor/cafeteria-inventory/CreateCateteriaInventory";
import EditCateteriaInventory from "../../../components/vendor/cafeteria-inventory/EditCateteriaInventory";
import DeleteCateteriaInventory from "../../../components/vendor/cafeteria-inventory/DeleteCateteriaInventory";
import Modal from "../../../components/ui/utils/Modal";
import Button from "../../../components/ui/utils/Button";
import useFormInput from "../../../hooks/form/useFormInput";
import InputField from "../../../components/ui/form/InputField";
import { useSelector } from "react-redux";

const CafeteriaInventory = () => {
  const {
    currentPage,
    data,
    error,
    fetchData,
    limit,
    loading,
    setPage,
    setSorting,
    sort,
    totalItems,
    totalPages,
    setFilters,
  } = usePaginate({
    apiMethod: apiRequest.getVendorCompliance,
    dataKey: "coupons",
  });
  
  const { isOpen, openModal, closeModal, actionType } = useModal();
  const handleSort = (field) => {
    const order =
      sort.sortField === field && sort.sortOrder === "asc" ? "desc" : "asc";
    setSorting({ sortField: field, sortOrder: order });
  };
  // Filters
  const searchInitialValues = { code: "" };
  const { values, handleChange, setValues } = useFormInput(searchInitialValues);
  const debounceRef = useRef(null);
  useEffect(() => {
    if (values.code !== undefined) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        const filters = {};
        if (values.code) {
          filters.code = { $regex: values.code, $options: "i" };
        }
        setFilters(filters);
      }, 500);
      return () => clearTimeout(debounceRef.current);
    }
  }, [values.code]);

    const BRAND_NAME = useSelector(
    (state) => state.globalState?.siteSetting?.site_settings?.site_name
  );
  return (
    <Layout>
      <Helmet>
        <title>Cafeteria & Inventory - {BRAND_NAME??''}</title>
      </Helmet>
      <div className="p-4 bg-white rounded">
        <div className="flex items-center gap-1 py-2">
          <Button
            onClick={() => openModal({ type: "add" })}
            variant="primary"
            isLoading={false}
          >
            <div className="flex items-center justify-center gap-3">
              <FaPlus />
              <p>Add New</p>
            </div>
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px] gap-2 my-2">
          <InputField
            name="code"
            placeholder="e.g. Summer25"
            value={values.code}
            onChange={handleChange}
          />
          <button
            className="py-1 text-white rounded bg-emerald-600 hover:bg-emerald-800"
            onClick={() => setValues(searchInitialValues)}
          >
            Clear
          </button>
        </div>
        <Table
          headers={[
            { label: "Coupon", key: "code", sorting: true },
            { label: "Discount", key: "discount", sorting: false },
            { label: "Valid Till", key: "validUntil", sorting: false },
            { label: "Valid From", key: "validFrom", sorting: false },
            { label: "Used", key: "usedCount", sorting: false },
            { label: "Status", key: "status", sorting: true },
            {
              label: "Action",
              key: "action",
              sorting: false,
            },
          ]}
          loading={loading}
          handleSort={handleSort}
          sort={sort}
          error={error}
          cols={7}
          renderRow={
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.map((coupon) => {
                const now = new Date();
                const isExpired =
                  coupon?.validUntil && new Date(coupon.validUntil) < now;

                return (
                  <tr
                    key={coupon._id}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold text-gray-800">
                          {coupon.code}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-gray-500">
                        {coupon.discountType === "percentage"
                          ? `${coupon.discountValue}% Off`
                          : `₹${coupon.discountValue} Off`}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-gray-400">
                        {new Date(coupon.validFrom).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-gray-400">
                        {new Date(coupon.validUntil).toLocaleDateString()}
                      </span>
                    </td>

                    <td className="px-3 py-3">{coupon.usedCount}</td>
                    <td className="px-3 py-3">
                      <span
                        className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                          isExpired
                            ? "bg-gray-200 text-gray-500"
                            : coupon.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {isExpired
                          ? "Expired"
                          : coupon.isActive
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </td>

                    <td className="px-3 py-3">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() =>
                            openModal({
                              type: "edit",
                              data: { state: coupon },
                            })
                          }
                          className="text-primary"
                          title="Edit"
                        >
                          <FaEdit size={18} />
                        </button>
                        <button
                          onClick={() =>
                            openModal({
                              type: "delete",
                              data: { state: coupon },
                            })
                          }
                          className="text-red-500"
                          title="Delete"
                        >
                          <FaTrashAlt size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          }
        />
        {totalPages > 1 && (
          <Pagination
            pagination={{
              currentPage,
              totalPages,
              totalItems: totalItems,
              perPage: limit,
            }}
            handlePageChange={setPage}
            loading={loading}
          />
        )}
      </div>
      <Modal isOpen={isOpen} onClose={closeModal} size="lg" overflow={""}>
        {actionType?.type === "add" && (
          <CreateCateteriaInventory closeModal={closeModal} callback={fetchData} />
        )}
        {actionType?.type === "edit" && (
          <EditCateteriaInventory
            data={actionType?.data?.state}
            closeModal={closeModal}
            callback={fetchData}
          />
        )}
        {actionType?.type === "delete" && (
          <DeleteCateteriaInventory
            data={actionType?.data?.state}
            closeModal={closeModal}
            callback={fetchData}
          />
        )}
      </Modal>
    </Layout>
  );
};

export default CafeteriaInventory;
